import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ActionsPanel, FlowEditor, NameAFlow, adminApi, formatIst, type BotFlowSummary } from '@wa/shared';
import { Page, DataTable, Cell, Empty } from '@/components/Primitives';


/**
 * Bots, per client.
 *
 * Platform-only, for the same reason template authoring is: a careless flow messages
 * a client's customers and burns their number's quality rating. Actions additionally
 * hold credentials for a client's own systems, which is the other half of why this is
 * not a client screen.
 */
export function BotsPage() {
  const queryClient = useQueryClient();
  const [tenantId, setTenantId] = useState<number | null>(null);
  const [openFlowId, setOpenFlowId] = useState<number | null>(null);
  const [naming, setNaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tenants = useQuery({ queryKey: ['tenants'], queryFn: adminApi.tenants });

  const chosen = tenantId ?? tenants.data?.[0]?.id ?? null;

  const flows = useQuery({
    queryKey: ['flows', chosen],
    queryFn: () => adminApi.flows(chosen!),
    enabled: chosen !== null,
  });

  const actions = useQuery({
    queryKey: ['bot-actions', chosen],
    queryFn: () => adminApi.botActions(chosen!),
    enabled: chosen !== null,
  });

  const sessions = useQuery({
    queryKey: ['bot-sessions', chosen],
    queryFn: () => adminApi.botSessions(chosen!),
    enabled: chosen !== null,
  });

  const open = useQuery({
    queryKey: ['flow', openFlowId],
    queryFn: () => adminApi.flow(openFlowId!),
    enabled: openFlowId !== null,
  });

  const act = (fn: () => Promise<unknown>, fallback: string) => async () => {
    setError(null);
    try {
      await fn();
      void queryClient.invalidateQueries({ queryKey: ['flows'] });
    } catch (e) {
      setError(e instanceof Error ? e.message : fallback);
    }
  };

  const create = useMutation({
    mutationFn: (name: string) => adminApi.createFlow(chosen!, { name }),
    onSuccess: (created) => {
      setError(null);
      setNaming(false);
      void queryClient.invalidateQueries({ queryKey: ['flows'] });
      setOpenFlowId(created.id);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not create that flow.'),
  });

  if (openFlowId !== null && open.data) {
    return (
      <Page title="Bot flow" subtitle={`${open.data.flow.name} · version ${open.data.flow.version}`}>
        <FlowEditor
          flow={open.data.flow}
          actions={actions.data ?? []}
          // PSM's console addresses a flow by id across every tenant; the client's
          // portal reaches the tenant-scoped routes. Same editor, two addresses.
          api={{
            validate: (definition) => adminApi.validateFlow(open.data!.flow.tenantId, definition),
            save: (id, definition) => adminApi.saveFlow(id, { definition }),
            publish: (id) => adminApi.publishFlow(id),
            rehearse: (body) => adminApi.rehearseFlow(open.data!.flow.tenantId, body),
          }}
          onClose={() => setOpenFlowId(null)}
        />
      </Page>
    );
  }

  return (
    <Page
      title="Bots"
      subtitle="A flow answers on a number until it hands over to a person. One flow can be live per number, plus one fallback for the rest."
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-small">
            <span className="text-micro text-(--color-muted)">Client</span>
            <select
              value={chosen ?? ''}
              onChange={(e) => { setTenantId(Number(e.target.value)); setOpenFlowId(null); }}
              className="rounded border border-(--color-line) bg-(--color-paper) px-2 py-1 text-small"
            >
              {(tenants.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.companyName}</option>)}
            </select>
          </label>

          {!naming && (
            <button
              type="button"
              disabled={chosen === null}
              onClick={() => { setError(null); setNaming(true); }}
              className="ml-auto rounded bg-(--color-brand) px-3 py-1.5 text-small font-medium text-white disabled:opacity-40"
            >
              New flow
            </button>
          )}
        </div>

        {naming && (
          <NameAFlow
            busy={create.isPending}
            onCancel={() => setNaming(false)}
            onCreate={(name) => create.mutate(name)}
          />
        )}

        {error && (
          <p role="alert" className="rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
            {error}
          </p>
        )}

        <section>
          <h3 className="mb-2 text-small font-semibold">Flows</h3>
          {(flows.data ?? []).length === 0 ? (
            <Empty>No flows for this client yet.</Empty>
          ) : (
            <DataTable head={['Name', 'Version', 'On', 'State', 'Live now', 'Published', '']}>
              {(flows.data ?? []).map((flow: BotFlowSummary) => (
                <tr key={flow.id} className="border-t border-(--color-line-dim)">
                  <Cell>
                    <button type="button" onClick={() => setOpenFlowId(flow.id)} className="text-(--color-brand) hover:underline">
                      {flow.name}
                    </button>
                  </Cell>
                  <Cell mono>v{flow.version}</Cell>
                  <Cell>{flow.channelNumber ?? 'All numbers'}</Cell>
                  <Cell>
                    {flow.isEnabled ? 'Live' : flow.isPublished ? 'Published' : 'Draft'}
                  </Cell>
                  <Cell mono>{flow.liveSessions || ''}</Cell>
                  <Cell nowrap>{flow.publishedAt ? formatIst(flow.publishedAt) : '—'}</Cell>
                  <Cell>
                    {flow.isPublished && (
                      <button
                        type="button"
                        onClick={act(
                          () => (flow.isEnabled ? adminApi.disableFlow(flow.id) : adminApi.enableFlow(flow.id)),
                          'Could not change that.')}
                        className="rounded border border-(--color-line) px-2 py-0.5 text-micro"
                      >
                        {flow.isEnabled ? 'Take offline' : 'Put live'}
                      </button>
                    )}
                  </Cell>
                </tr>
              ))}
            </DataTable>
          )}
        </section>

        <ActionsPanel
          actions={actions.data ?? []}
          readOnly={chosen === null}
          api={{
            save: (action) => adminApi.saveBotAction(chosen!, action),
            remove: (id) => adminApi.deleteBotAction(id),
          }}
        />

        <section>
          <h3 className="mb-2 text-small font-semibold">Conversations with a bot right now</h3>
          <p className="mb-2 text-micro text-(--color-muted)">
            The first place to look when a bot misbehaves: which step somebody is stuck
            on, and what it has collected from them.
          </p>
          {(sessions.data ?? []).length === 0 ? (
            <Empty>Nobody is mid-flow.</Empty>
          ) : (
            <DataTable head={['Contact', 'Flow', 'Step', 'Steps taken', 'Last activity']}>
              {(sessions.data ?? []).map((s) => (
                <tr key={s.conversationId} className="border-t border-(--color-line-dim)">
                  <Cell>{s.contactName ?? `+${s.waId}`}</Cell>
                  <Cell>{s.flowName} v{s.flowVersion}</Cell>
                  <Cell mono>{s.state === '__HUMAN__' ? 'with a person' : s.state}</Cell>
                  <Cell mono>{s.stepCount}</Cell>
                  <Cell nowrap>{formatIst(s.updatedAt)}</Cell>
                </tr>
              ))}
            </DataTable>
          )}
        </section>
      </div>
    </Page>
  );
}
